/**
 * lazyWithRetry — drop-in replacement for React.lazy() that:
 *   1. Retries a failed chunk import up to 3 times with exponential backoff.
 *   2. On persistent ChunkLoadError, force-reloads the page exactly ONCE
 *      (stored in sessionStorage to prevent infinite reload loops).
 *   3. Preserves the original API so existing routes don't change.
 *
 * This eliminates the most common cause of "Something went wrong" screens
 * after a deployment: users whose service-worker cache served a stale
 * /static/js/xyz.chunk.js that 404s against the new build manifest.
 */
import { lazy } from 'react';

const RELOAD_KEY = 'pie:chunk-reloaded-at';
const RELOAD_WINDOW_MS = 30_000; // only one forced reload per 30s

const isChunkLoadError = (err) => {
  if (!err) return false;
  const msg = String(err.message || err);
  return (
    err.name === 'ChunkLoadError' ||
    /Loading chunk [\w-]+ failed/.test(msg) ||
    /Loading CSS chunk [\w-]+ failed/.test(msg) ||
    /Failed to fetch dynamically imported module/.test(msg) ||
    /Importing a module script failed/.test(msg)
  );
};

const retryImport = (factory, retriesLeft = 3, delayMs = 400) =>
  factory().catch((err) => {
    if (!isChunkLoadError(err) || retriesLeft <= 0) throw err;
    return new Promise((resolve) => setTimeout(resolve, delayMs)).then(() =>
      retryImport(factory, retriesLeft - 1, delayMs * 2)
    );
  });

export default function lazyWithRetry(factory) {
  return lazy(() =>
    retryImport(factory).catch((err) => {
      if (isChunkLoadError(err)) {
        // Force a single hard reload to pull fresh assets. Protect against
        // reload loops by throttling via sessionStorage.
        try {
          const last = parseInt(sessionStorage.getItem(RELOAD_KEY) || '0', 10);
          if (Date.now() - last > RELOAD_WINDOW_MS) {
            sessionStorage.setItem(RELOAD_KEY, String(Date.now()));
            // Best-effort cache bust so the new request bypasses any stale SW
            if ('caches' in window) {
              caches.keys().then((names) =>
                Promise.all(names.map((n) => caches.delete(n)))
              ).finally(() => window.location.reload());
            } else {
              window.location.reload();
            }
            // Return a dummy component while the reload is happening so React
            // doesn't render an error first
            return { default: () => null };
          }
        } catch (_) { /* fall through */ }
      }
      throw err;
    })
  );
}
